import { useMemo } from 'react';

import { useMembersOnlyInformation } from './useMembersOnlyInformation';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { Price, StyleVariant } from '~/utilities/graphql/codegen';

export type PriceInput = Price | number | null | undefined;

const parseNumeric = (val?: string | number | null): number | null => {
  if (val == null) {
    return null;
  }

  if (typeof val === 'number') {
    return Number.isFinite(val) ? val : null;
  }

  const num = parseFloat(val);

  if (Number.isFinite(num)) {
    return num;
  }

  return null;
};

const getNumeric = (input: PriceInput, fallback?: string | null): number | null => {
  const parsedFallback = parseNumeric(fallback);

  if (parsedFallback != null) {
    return parsedFallback;
  }

  if (typeof input === 'number') {
    return input;
  }

  return input?.value || null;
};

type ParamType = {
  price?: PriceInput;
  personalisedPrice?: PriceInput;
  productStyleVariantInfo?: StyleVariant | null;
};

type ReturnType = {
  priceValue: number | null;
  priceFormatted: string | null;
  applyPersonalisedPrice: boolean;
};

export const useMembersOnlyPrices = ({
  price,
  personalisedPrice,
  productStyleVariantInfo,
}: ParamType): ReturnType => {
  const { priceFormatter } = useStaticContext();

  const baseValue = getNumeric(price, productStyleVariantInfo?.unformattedBasePrice);
  const personalValue = getNumeric(
    personalisedPrice,
    productStyleVariantInfo?.unformattedPersonalisedPrice
  );

  const { applyPersonalisedPrice } = useMembersOnlyInformation({
    personalisedPrice,
    styleVariantPersonalisedPrice: productStyleVariantInfo?.unformattedPersonalisedPrice,
  });

  return useMemo(() => {
    const getFormatted = (input: PriceInput, fallback?: string | null, numeric?: number | null) => {
      if (fallback) {
        return fallback;
      }

      if (typeof input === 'object' && input?.formattedValue) {
        return input.formattedValue;
      }

      if (numeric) {
        return priceFormatter(numeric);
      }

      return null;
    };

    const priceValue = applyPersonalisedPrice ? personalValue : baseValue;
    const priceFormatted = applyPersonalisedPrice
      ? getFormatted(
          personalisedPrice,
          productStyleVariantInfo?.formattedPersonalisedPrice,
          personalValue
        )
      : getFormatted(price, productStyleVariantInfo?.formattedBasePrice, baseValue);

    return { priceValue, priceFormatted, applyPersonalisedPrice };
  }, [
    applyPersonalisedPrice,
    baseValue,
    priceFormatter,
    personalValue,
    personalisedPrice,
    price,
    productStyleVariantInfo?.formattedBasePrice,
    productStyleVariantInfo?.formattedPersonalisedPrice,
  ]);
};
